import type {
  AdminCustomer,
  ApiKeyList,
  App,
  AppList,
  Audience,
  AuditLogList,
  ChangePasswordRequest,
  Channel,
  CreateApiKeyInput,
  CreateAppRequest,
  CreateCredentialProfileRequest,
  CreateCredentialRequest,
  CreatedApiKey,
  CreateProfileCredentialInput,
  Credential,
  CredentialList,
  CredentialProfileDetail,
  CredentialProfileList,
  CredentialTestResult,
  CustomerList,
  DashboardConfig,
  DeliveryList,
  DeliveryLogDetail,
  DeliveryLogList,
  DeliveryLogQuery,
  GeoOverview,
  LoginRequest,
  Me,
  MessageCreated,
  MessageDetail,
  MessageList,
  Overview,
  ProfileCredential,
  Provider,
  ProviderList,
  RetryInput,
  RetryPreview,
  RetryResult,
  RunRetryInput,
  SendMessageInput,
  Subscription,
  SystemSettingsView,
  UpdateAppRequest,
  UpdateCredentialProfileRequest,
  UpdateCredentialRequest,
  UpdateProfileCredentialRequest,
  UpdateProfileRequest,
  UpdateProviderRequest,
  UpdateSystemSettingsRequest,
} from "@aiyo/shared";
import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, del, get, patch, post } from "./api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      // Client errors will not fix themselves, so only retry network and server errors.
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

export const keys = {
  me: ["me"] as const,
  apps: ["apps"] as const,
  app: (id: string) => ["apps", id] as const,
  apiKeys: (appId: string) => ["apps", appId, "api-keys"] as const,
  messages: (appId: string) => ["apps", appId, "messages"] as const,
  message: (appId: string, id: string) => ["apps", appId, "messages", id] as const,
  deliveries: (appId: string, id: string) => ["apps", appId, "messages", id, "deliveries"] as const,
  providers: ["providers"] as const,
  credentials: ["credentials"] as const,
  profiles: ["credential-profiles"] as const,
  profile: (id: string) => ["credential-profiles", id] as const,
  audit: ["audit"] as const,
  settings: ["settings"] as const,
  overview: (q: OverviewParams) => ["overview", q] as const,
  geo: (q: GeoParams) => ["geo", q] as const,
  customers: ["customers"] as const,
  customerList: (q: CustomerFilters) => ["customers", "list", q] as const,
  customer: (id: string) => ["customers", id] as const,
  deliveryLog: ["delivery-log"] as const,
  deliveryLogList: (q: DeliveryLogFilters) => ["delivery-log", "list", q] as const,
  deliveryLogItem: (id: string) => ["delivery-log", id] as const,
  audience: (appId: string, channels: Channel[]) => ["apps", appId, "audience", channels] as const,
  retryPreview: (body: RetryInput) => ["retry-preview", body] as const,
};

export type CustomerFilters = { app_id?: string; q?: string; city?: string };
export type GeoParams = { app_id?: string; days: number; include_test?: boolean };
export type OverviewParams = { app_id?: string; days: number; tz: string; include_test?: boolean };
export type DeliveryLogFilters = Partial<Record<keyof DeliveryLogQuery, string>>;

/** Query string from an object, skipping empty values. */
export function queryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

const cursorParam = (cursor: string) => (cursor ? `&cursor=${encodeURIComponent(cursor)}` : "");

// --- Auth -------------------------------------------------------------------

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => get<Me>("/admin/auth/me"),
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => post<Me>("/admin/auth/login", body),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProfileRequest) => patch<Me>("/admin/auth/me", body),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => post<null>("/admin/auth/password", body),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<null>("/admin/auth/logout"),
    onSettled: () => qc.clear(),
  });
}

// --- Apps -------------------------------------------------------------------

export function useApps() {
  return useQuery({ queryKey: keys.apps, queryFn: () => get<AppList>("/admin/apps") });
}

export function useApp(appId: string) {
  return useQuery({ queryKey: keys.app(appId), queryFn: () => get<App>(`/admin/apps/${appId}`) });
}

export function useCreateApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAppRequest) => post<App>("/admin/apps", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.apps }),
  });
}

export function useUpdateApp(appId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAppRequest) => patch<App>(`/admin/apps/${appId}`, body),
    onSuccess: (app) => {
      qc.setQueryData(keys.app(appId), app);
      void qc.invalidateQueries({ queryKey: keys.apps });
      void qc.invalidateQueries({ queryKey: keys.profiles });
      void qc.invalidateQueries({ queryKey: keys.credentials });
    },
  });
}

export function useDeleteApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (appId: string) => del<null>(`/admin/apps/${appId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.apps }),
  });
}

// --- API keys ---------------------------------------------------------------

export function useApiKeys(appId: string) {
  return useQuery({
    queryKey: keys.apiKeys(appId),
    queryFn: () => get<ApiKeyList>(`/admin/apps/${appId}/api-keys`),
  });
}

export function useCreateApiKey(appId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateApiKeyInput) => post<CreatedApiKey>(`/admin/apps/${appId}/api-keys`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.apiKeys(appId) }),
  });
}

export function useRevokeApiKey(appId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => del(`/admin/apps/${appId}/api-keys/${keyId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.apiKeys(appId) }),
  });
}

// --- Credential profiles (D-44) -----------------------------------------------

function invalidateProfiles(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: keys.profiles });
  void qc.invalidateQueries({ queryKey: keys.credentials });
  void qc.invalidateQueries({ queryKey: keys.apps });
}

export function useProfiles() {
  return useQuery({
    queryKey: keys.profiles,
    queryFn: () => get<CredentialProfileList>("/admin/credential-profiles"),
  });
}

export function useProfile(id: string | undefined) {
  return useQuery({
    queryKey: keys.profile(id ?? ""),
    queryFn: () => get<CredentialProfileDetail>(`/admin/credential-profiles/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCredentialProfileRequest) =>
      post<CredentialProfileDetail>("/admin/credential-profiles", body),
    onSuccess: () => invalidateProfiles(qc),
  });
}

export function useUpdateCredentialProfile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCredentialProfileRequest) =>
      patch<CredentialProfileDetail>(`/admin/credential-profiles/${id}`, body),
    onSuccess: (profile) => {
      qc.setQueryData(keys.profile(id), profile);
      invalidateProfiles(qc);
    },
  });
}

export function useDeleteProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<null>(`/admin/credential-profiles/${id}`),
    onSuccess: () => invalidateProfiles(qc),
  });
}

export function useCreateProfileEntry(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProfileCredentialInput) =>
      post<ProfileCredential>(`/admin/credential-profiles/${profileId}/credentials`, body),
    onSuccess: () => invalidateProfiles(qc),
  });
}

export function useUpdateProfileEntry(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateProfileCredentialRequest & { id: string }) =>
      patch<ProfileCredential>(`/admin/credential-profiles/${profileId}/credentials/${id}`, body),
    onSuccess: () => invalidateProfiles(qc),
  });
}

export function useDeleteProfileEntry(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<null>(`/admin/credential-profiles/${profileId}/credentials/${id}`),
    onSuccess: () => invalidateProfiles(qc),
  });
}

// --- Providers and credentials ------------------------------------------------

export function useProviders() {
  return useQuery({
    queryKey: keys.providers,
    queryFn: () => get<ProviderList>("/admin/providers"),
    staleTime: 60_000,
  });
}

export function useUpdateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateProviderRequest & { id: string }) =>
      patch<Provider>(`/admin/providers/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.providers });
      void qc.invalidateQueries({ queryKey: keys.credentials });
    },
  });
}

export function useCredentials() {
  return useQuery({ queryKey: keys.credentials, queryFn: () => get<CredentialList>("/admin/credentials") });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCredentialRequest) => post<Credential>("/admin/credentials", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

export function useUpdateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateCredentialRequest & { id: string }) =>
      patch<Credential>(`/admin/credentials/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<null>(`/admin/credentials/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

export function useTestCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<CredentialTestResult>(`/admin/credentials/${id}/test`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

// --- Messages -----------------------------------------------------------------

export function useMessages(appId: string) {
  return useInfiniteQuery({
    queryKey: keys.messages(appId),
    queryFn: ({ pageParam }) =>
      get<MessageList>(`/admin/apps/${appId}/messages?limit=25${cursorParam(pageParam)}`),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: 10_000,
  });
}

const ACTIVE_STATUSES = new Set(["scheduled", "queued", "processing"]);

export function useMessage(appId: string, messageId: string, enabled = true) {
  return useQuery({
    queryKey: keys.message(appId, messageId),
    queryFn: () => get<MessageDetail>(`/admin/apps/${appId}/messages/${messageId}`),
    refetchInterval: (q) => (q.state.data && ACTIVE_STATUSES.has(q.state.data.status) ? 2_000 : false),
    enabled: enabled && Boolean(appId && messageId),
  });
}

export function useDeliveries(appId: string, messageId: string, live: boolean) {
  return useInfiniteQuery({
    queryKey: keys.deliveries(appId, messageId),
    queryFn: ({ pageParam }) =>
      get<DeliveryList>(
        `/admin/apps/${appId}/messages/${messageId}/deliveries?limit=50${cursorParam(pageParam)}`,
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: live ? 2_000 : false,
  });
}

export function useSendMessage(appId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, testMode }: { body: SendMessageInput; testMode: boolean }) =>
      post<MessageCreated>(`/admin/apps/${appId}/messages${testMode ? "?test_mode=true" : ""}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.messages(appId) }),
  });
}

/** Who a broadcast on these channels would reach now (D-52). */
export function useAudience(appId: string, channels: Channel[], enabled = true) {
  return useQuery({
    queryKey: keys.audience(appId, channels),
    queryFn: () =>
      get<Audience>(`/admin/apps/${appId}/audience${queryString({ channels: channels.join(",") })}`),
    enabled: enabled && Boolean(appId) && channels.length > 0,
    placeholderData: keepPreviousData,
  });
}

// --- Manual retry (D-51) --------------------------------------------------------

export function useRetryPreview(body: RetryInput, enabled: boolean) {
  return useQuery({
    queryKey: keys.retryPreview(body),
    queryFn: () => post<RetryPreview>("/admin/deliveries/retry/preview", body),
    enabled,
    staleTime: 0,
    gcTime: 0,
    placeholderData: keepPreviousData,
  });
}

export function runRetryBatch(body: RunRetryInput) {
  return post<RetryResult>("/admin/deliveries/retry", body);
}

/** Retried deliveries change messages, the delivery log and customers' activity. */
export function invalidateAfterRetry(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: keys.deliveryLog });
  void qc.invalidateQueries({ queryKey: keys.customers });
  void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "apps" && q.queryKey[2] === "messages" });
}

// --- Audit and settings -------------------------------------------------------

export function useAuditLogs(entityType?: string) {
  return useInfiniteQuery({
    queryKey: [...keys.audit, entityType ?? ""],
    queryFn: ({ pageParam }) =>
      get<AuditLogList>(
        `/admin/audit-logs${queryString({ limit: 50, entity_type: entityType, cursor: pageParam })}`,
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** Runtime dashboard configuration (map styles), readable by every signed-in user (D-45). */
export function useDashboardConfig() {
  return useQuery({
    queryKey: ["dashboard-config"],
    queryFn: () => get<DashboardConfig>("/admin/config"),
    staleTime: 5 * 60_000,
  });
}

export function useSettings(enabled = true) {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => get<SystemSettingsView>("/admin/settings"),
    enabled,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSystemSettingsRequest) => patch<SystemSettingsView>("/admin/settings", body),
    onSuccess: (view) => {
      qc.setQueryData(keys.settings, view);
      void qc.invalidateQueries({ queryKey: keys.audit });
      void qc.invalidateQueries({ queryKey: ["dashboard-config"] });
    },
  });
}

// --- Overview, customers and delivery log ------------------------------------

export function useOverview(q: OverviewParams) {
  return useQuery({
    queryKey: keys.overview(q),
    queryFn: () =>
      get<Overview>(
        `/admin/stats/overview${queryString({ app_id: q.app_id, days: q.days, tz: q.tz, include_test: q.include_test })}`,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export function useGeo(q: GeoParams) {
  return useQuery({
    queryKey: keys.geo(q),
    queryFn: () =>
      get<GeoOverview>(
        `/admin/stats/geo${queryString({ app_id: q.app_id, days: q.days, include_test: q.include_test })}`,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 120_000,
  });
}

export function useCustomers(q: CustomerFilters) {
  return useInfiniteQuery({
    queryKey: keys.customerList(q),
    queryFn: ({ pageParam }) =>
      get<CustomerList>(`/admin/customers${queryString({ ...q, limit: 50, cursor: pageParam })}`),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: keys.customer(id),
    queryFn: () => get<AdminCustomer>(`/admin/customers/${id}`),
  });
}

export function useEraseCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<null>(`/admin/customers/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.customers });
      void qc.invalidateQueries({ queryKey: keys.deliveryLog });
    },
  });
}

export function useUpdateSubscription(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "unsubscribed" }) =>
      patch<Subscription>(`/admin/customers/${customerId}/subscriptions/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.customers }),
  });
}

export function useDeliveryLog(q: DeliveryLogFilters, options: { live?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: keys.deliveryLogList(q),
    queryFn: ({ pageParam }) =>
      get<DeliveryLogList>(`/admin/deliveries${queryString({ ...q, limit: 50, cursor: pageParam })}`),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchInterval: options.live ? 5_000 : false,
  });
}

export function useDeliveryLogItem(id: string | null) {
  return useQuery({
    queryKey: keys.deliveryLogItem(id ?? ""),
    queryFn: () => get<DeliveryLogDetail>(`/admin/deliveries/${id}`),
    enabled: Boolean(id),
  });
}
